import { Injectable } from '@angular/core';
import { ProjectDataService } from '../service/project-data.service';
import { ProjectModel } from '../model/project.model';
import { lastValueFrom, Observable, of, timer } from 'rxjs';
import { ProjectFinalizationInfoVO, ProjectVO } from '../model/vo/project.vo';
import { ReportGenerationPropertyVO } from '../components/article/model/vo/property.vo';
import { GenerationResponseVO } from '../manager/generation-response.vo';
import { ReportVO } from '../model/vo/report.vo';
import { CameraParametersVO } from '@3d-scene/camera-parameters.vo';
import { UserProfileVO } from '@user/model/vo/user.vo';
import { first, mergeMap } from 'rxjs/operators';
import { ProjectFinalizationInfoDTO } from '../model/vo/project-finalization-info.dto';
import { ProjectService } from '../components/import-export/service/project.service';
import { JsonInfoService } from '../service/json-info.service';

@Injectable({
  providedIn: 'root'
})
export class ProjectFinalizationService {

  constructor(
    private projectModel: ProjectModel,
    private projectDataService: ProjectDataService,
    private projectService: ProjectService,
    private jsonInfoService: JsonInfoService,
  ) {}

  public async resetCurrentProjectForPdmExport(): Promise<void> {
    const projectHead = await lastValueFrom(this.projectDataService.resetProjectFinalization(this.projectModel.currentProject.id));
    this.projectModel.setFinalizationInfos(projectHead);
  }

  public createFinalizationPreviewHandler(data: {
    reports: ReportVO[];
    cameraParameters: CameraParametersVO;
    projectFinalizationInfo: ProjectFinalizationInfoVO;
    project: ProjectVO;
    locale: string;
    projectId: number;
    userProfile: UserProfileVO;
    reportProperties: ReportGenerationPropertyVO[]
  }): Observable<GenerationResponseVO> {
    return this.checkFinalizationPreviewGenerationResult(this.projectService.createFinalizationPreviewHandler(data), data.projectFinalizationInfo);
  }

  private checkFinalizationPreviewGenerationResult(request: Observable<GenerationResponseVO>,
                                                   projectFinalizationInfo: ProjectFinalizationInfoVO) : Observable<GenerationResponseVO> {
    return request.pipe(
      mergeMap(response => {
        if (response.contentGenerated) {
          // If first request has generated the response use it.
          return of(response);
        } else {
          // If there is no generated response check every 5 seconds.
          return timer(5000, 5000).pipe(
            mergeMap(() => this.checkPreviewCreationResultCallFunction(response.contentId, this.projectModel.currentProject, projectFinalizationInfo)),
            first(response => response.contentGenerated));
        }
      })
    );
  }

  private checkPreviewCreationResultCallFunction(contentId: string, project: ProjectVO, projectFinalizationInfoVO: ProjectFinalizationInfoVO): Observable<GenerationResponseVO> {
    const dto: ProjectFinalizationInfoDTO = new ProjectFinalizationInfoDTO();
    dto.projectId = project.id;
    dto.projectVersion = project.currentVersion;
    dto.reportId = contentId;
    dto.projectFinalizationInfoVO = projectFinalizationInfoVO;
    if (project.projectType === 'LTL') {
      dto.jsonInfoString = this.jsonInfoService.createProjectInfoJson(project);
    }
    return this.projectDataService.checkFinalizationPreviewGenerationResult(dto);
  }
}
